import React, { useCallback, useEffect, useState } from "react";
import { useField } from "formik";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
} from "@mui/material";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

interface DropdownSelectorProps {
  label: string;
  name: string;
  optionLabel: string | string[];
  optionSeparator?: string;
  parentOptions?: any[];
  fetchFunction: (params: any) => Promise<any>;
  fetchParams?: any;
  keyField?: string;
  value?: any;
  disabled?: boolean;
  onChange?: (event: SelectChangeEvent<any>, option: any) => void;
}

// Helper function to get nested values using dot notation
const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const DropdownSelector: React.FC<DropdownSelectorProps> = ({
  label,
  optionLabel,
  optionSeparator = " ",
  parentOptions,
  fetchFunction,
  fetchParams,
  keyField = "id",
  ...props
}) => {
  const [field, meta] = useField(props.name);
  const [options, setOptions] = useState<any[]>([]);
  const { openSnack } = useSnack();

  const fetchData = useCallback(async () => {
    try {
      const res = await fetchFunction(fetchParams);
      setOptions(res.data);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  }, [fetchFunction, fetchParams, openSnack]);

  useEffect(() => {
    if (parentOptions) {
      setOptions(parentOptions);
    } else {
      fetchData();
    }
  }, [fetchFunction, fetchParams, parentOptions, fetchData]);

  const getOptionLabel = (option: any) => {
    if (Array.isArray(optionLabel)) {
      return optionLabel
        .map((label) => option[label] || "")
        .join(optionSeparator);
    }
    if (typeof optionLabel === "string" && optionLabel.includes(".")) {
      return getNestedValue(option, optionLabel) || option.name;
    }
    return option[optionLabel] || option.name;
  };

  return (
    <FormControl
      fullWidth
      variant="outlined"
      error={meta.touched && Boolean(meta.error)}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        {...field}
        {...props}
        value={field.value ?? ""}
        onChange={(event) => {
          field.onChange(event);
          if (props.onChange) {
            props.onChange(
              event,
              options.find(
                (opt) =>
                  opt[keyField] === event.target.value ||
                  String(opt[keyField]) === String(event.target.value)
              )
            );
          }
        }}
        onBlur={field.onBlur}
      >
        {options &&
          Array.isArray(options) &&
          options.map((option) => (
            <MenuItem key={option[keyField]} value={option[keyField]}>
              {getOptionLabel(option)}
            </MenuItem>
          ))}
      </Select>
      {meta.touched && meta.error ? (
        <FormHelperText>{meta.error}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export default DropdownSelector; 